import { useEffect, useState } from "react";
import type { Skill } from "@cowork/shared";
import { cn } from "../../lib/utils.js";

const ENGINE_BASE = import.meta.env["VITE_ENGINE_HTTP_URL"] ?? "http://localhost:3001";

async function fetchSkills(): Promise<Skill[]> {
  try {
    const res = await fetch(`${ENGINE_BASE}/skills`);
    if (!res.ok) return [];
    return (await res.json()) as Skill[];
  } catch {
    return [];
  }
}

export function SkillsBrowser() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Skill | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSkills()
      .then(setSkills)
      .finally(() => setLoading(false));
  }, []);

  const filtered = skills.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.description.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex h-full">
      {/* List */}
      <div className="w-64 border-r border-[var(--border)] flex flex-col">
        <div className="p-3 border-b border-[var(--border)]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills…"
            className="w-full text-xs rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] text-[var(--text-primary)] px-3 py-1.5 outline-none placeholder:text-[var(--text-secondary)]"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-xs text-[var(--text-secondary)]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-xs text-[var(--text-secondary)]">No skills found</div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.name}
                onClick={() => setSelected(s)}
                className={cn(
                  "w-full text-left px-3 py-2.5 border-b border-[var(--border)] transition-colors",
                  selected?.name === s.name
                    ? "bg-[var(--bg-tertiary)]"
                    : "hover:bg-[var(--bg-secondary)]",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[var(--text-primary)]">
                    /{s.name}
                  </span>
                  <span
                    className={cn(
                      "text-[10px] px-1.5 py-0.5 rounded-full",
                      s.source === "bundled"
                        ? "bg-[var(--accent-muted)] text-[var(--accent)]"
                        : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)]",
                    )}
                  >
                    {s.source}
                  </span>
                </div>
                <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 truncate">
                  {s.description}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 p-6 overflow-y-auto">
        {selected ? (
          <>
            <div className="mb-4">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">
                /{selected.name}
              </h2>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                {selected.description}
              </p>
              {selected.tags && selected.tags.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {selected.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-4">
              <h3 className="text-xs font-medium text-[var(--text-secondary)] mb-2">
                PROMPT
              </h3>
              <pre className="text-xs text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed font-mono">
                {selected.content}
              </pre>
            </div>
            {selected.filePath && (
              <p className="text-[10px] text-[var(--text-secondary)] mt-2">
                {selected.filePath}
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="mb-3" style={{ color: "var(--text-tertiary)" }}>
              <polygon points="16,2 20,12 30,12 22,19 25,29 16,23 7,29 10,19 2,12 12,12" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
            </svg>
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Skills
            </h3>
            <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-xs">
              Skills are pre-built prompts you can invoke with /{"{skill-name}"} in chat.
              Add custom skills to ~/.cowork/skills/*.md
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
